/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { MaterialReactTable } from "material-react-table";

import AutomationScheduleHeader from "components/AutomationComponents/AutomationScheduleListBody/Header/AutomationScheduleHeader";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import AutomationTaskDetailPopup from "components/AutomationComponents/AutomationScheduleListBody/Popup/AutomationTaskDetailPopup";
import AutomationScheduleFilterPopup from "components/AutomationComponents/AutomationScheduleListBody/Popup/AutomationScheduleFilterPopup";

import { automationHeaderLinks } from "helper/AutomationHelper/AutomationMenuLinks";

/*------ import url and method ------*/
import * as url from "helper/UrlHelper";
import { getData } from "utils/Gateway";

/*------ import assets ------*/
import assetImages from "constants/images";

const AutomationScheduleListBody = () => {
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const { t, i18n } = useTranslation();

  const commonHeaderObject = automationHeaderLinks(t);

  const [isLoading, setIsLoading] = useState(false);
  const [taskList, setTaskList] = useState([]);

  const [rowSelection, setRowSelection] = useState({});
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [reloadData, setReloadData] = useState(false);

  // Detail popup
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);

  // Filter
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [isFilterReset, setIsFilterReset] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [lastPagination, setLastPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Alert
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // ── Fetch all scheduled tasks ──────────────────────────────
  const getAllScheduledTasks = async () => {
    setSelectedItemIds([]);
    setRowSelection({});
    try {
      setIsLoading(true);
      const requestUrl =
        url.API_BASE_URL + url.API_GET_AUTOMATION_TASK_SCHEDULES + `?token=${token}`;
      const response = await getData(requestUrl);
      setIsLoading(false);

      if (response.status) {
        setMessageType("success");
        setTaskList(response.data);
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (error) {
      console.error("Error fetching scheduled tasks:", error);
      setAlertMessage(error.message);
      setMessageType("error");
    }
    setShowAlert(true);
  };

  const resetFilterData = () => {
    setReloadData(true);
    setLastPagination({ pageIndex: 0, pageSize: 10 });
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  const handleViewTaskDetails = (task) => {
    setSelectedTaskId(task._id);
    setShowDetailPopup(true);
  };

  const handleCloseDetailPopup = () => {
    setShowDetailPopup(false);
    setSelectedTaskId(null);
  };

  useEffect(() => {
    getAllScheduledTasks();
  }, []);

  useEffect(() => {
    if (reloadData) {
      getAllScheduledTasks();
      setReloadData(false);
    }
  }, [reloadData]);

  // ── Apply client-side filter ───────────────────────────────
  const filteredTaskList = useMemo(() => {
    if (!jsonFilterQuery || jsonFilterQuery.length === 0) return taskList;

    return taskList.filter((task) =>
      jsonFilterQuery.every(({ field, operator, value }) => {
        const raw = task[field];

        // multi-select: value is [{label, value}]
        if (Array.isArray(value) && value[0]?.value !== undefined) {
          const vals = value.map((v) => v.value);
          return operator === "not_in" ? !vals.includes(raw) : vals.includes(raw);
        }

        const taskStr = String(raw ?? "").toLowerCase();
        const valStr  = String(value ?? "").toLowerCase();

        switch (operator) {
          case "contains":     return taskStr.includes(valStr);
          case "not_contains": return !taskStr.includes(valStr);
          case "equals":
          case "eq":           return taskStr === valStr;
          case "not_equals":   return taskStr !== valStr;
          case "starts_with":  return taskStr.startsWith(valStr);
          case "ends_with":    return taskStr.endsWith(valStr);
          case "gt":           return raw > value;
          case "lt":           return raw < value;
          case "gte":          return raw >= value;
          case "lte":          return raw <= value;
          case "between":
            return Array.isArray(value) && raw >= value[0] && raw <= value[1];
          default:             return true;
        }
      })
    );
  }, [taskList, jsonFilterQuery]);

  // ── Table columns ──────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <Link
            className="action_btn_mui"
            to="#"
            onClick={(e) => {
              e.preventDefault();
              handleViewTaskDetails(row.original);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </Link>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        accessorKey: "customid",
        header: t("ID"),
        Cell: ({ row }) => (
          <span className="text-muted">{row.original.customid || t("N/A")}</span>
        ),
      },
      {
        accessorKey: "tasktitle",
        header: t("Task Title"),
        Cell: ({ row }) => (
          <span className="fw-bold d-block">{row.original.title || t("N/A")}</span>
        ),
      },
      {
        accessorKey: "executiontype",
        header: t("Execution Type"),
        Cell: ({ row }) => {
          const type = row.original.executiontype;
          const isReactive = type === "reactive";
          return (
            <span
              className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}
            >
              {isReactive ? t("Reactive") : t("Proactive")}
            </span>
          );
        },
      },
      {
        accessorKey: "childstartdate",
        header: t("Schedule Date"),
        Cell: ({ row }) => {
          const d = row.original.childstartdate;
          return d ? (
            <div className="d-flex align-items-center gap-1">
              <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>
                calendar_today
              </span>
              <span style={{ fontSize: "13px" }}>{d}</span>
            </div>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        accessorKey: "childstarttime",
        header: t("Schedule Time"),
        Cell: ({ row }) => {
          const t2 = row.original.childstarttime;
          return t2 ? (
            <div className="d-flex align-items-center gap-1">
              <span className="material-symbols-outlined text-muted" style={{ fontSize: "14px" }}>
                schedule
              </span>
              <span style={{ fontSize: "13px" }}>{t2}</span>
            </div>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        accessorKey: "subtaskCount",
        header: t("Subtasks"),
        Cell: ({ row }) => (
          <span className="badge bg-primary">{row.original.subtaskCount || 0}</span>
        ),
      },
      {
        accessorKey: "moderator",
        header: t("Moderator"),
        Cell: ({ row }) =>
          row.original.moderator ? (
            <span>
              {row.original.moderator.name} {row.original.moderator.surname}
            </span>
          ) : (
            <span className="text-muted">{t("N/A")}</span>
          ),
      },
      {
        accessorKey: "createdAt",
        header: t("Created Date"),
        Cell: ({ row }) => (
          <div>
            <span>{new Date(row.original.createdAt).toLocaleDateString()}</span>
            <div className="text-muted" style={{ fontSize: "12px" }}>
              {new Date(row.original.createdAt).toLocaleTimeString()}
            </div>
          </div>
        ),
      },
    ],
    [i18n.language]
  );

  const columnOrder = ["#", "mrt-row-select", ...columns.map((c) => c.accessorKey)];

  const refreshRecords = () => resetFilterData();

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN"
  ) {
    return (
      <div id="content_wrapper">
        <section className="survey-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderObject}
            activeOption={t("Scheduled Automation Tasks")}
          />

          <div className="container-fluid px-lg-5">
            <AutomationScheduleHeader
              reloadList={() => {
                setJsonFilterQuery([]);
                setIsFilterReset(true);
                refreshRecords();
              }}
            />

            {isLoading ? (
              <div className="placeholder-glow d-flex flex-column gap-4">
                <span className="placeholder placeholder-lg bg-secondary col-12"></span>
                <span className="placeholder placeholder-lg bg-secondary col-8"></span>
                <span className="placeholder placeholder-lg bg-secondary col-4"></span>
              </div>
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={filteredTaskList}
                  enableGrouping
                  enableRowSelection
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  defaultColumn={{ minSize: 20, maxSize: 200, size: 50 }}
                  muiTableContainerProps={{ sx: { maxHeight: "60vh" } }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>
        </section>

        {/* Task Detail Popup */}
        <AutomationTaskDetailPopup
          taskId={selectedTaskId}
          isOpen={showDetailPopup}
          onClose={handleCloseDetailPopup}
        />

        {/* Filter Popup */}
        <AutomationScheduleFilterPopup
          setJsonFilterQuery={setJsonFilterQuery}
          reloadList={() => {
            setJsonFilterQuery([]);
            setIsFilterReset(true);
            refreshRecords();
          }}
          isFilterReset={isFilterReset}
          setIsFilterReset={setIsFilterReset}
        />
      </div>
    );
  } else {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <div className="empty_access text-center">
            <div className="empty_pic mb-4">
              <img src={assetImages.emptyVector} alt="" />
            </div>
            <div className="empty_text">
              <p className="fs-lg text-gray fw-semibold mb-4">
                {t("Sorry....! You don't have privilege to see this content")}
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }
};

export default AutomationScheduleListBody;
